import type { Container } from "pixi.js";
import { ExposureFilter } from "../../filters/ExposureFilter";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import { WinAmountLabel, type LabelBreakdown } from "./WinAmountLabel";
import type { WinAmountLabelFlareTuning } from "./WinAmountLabelFlareTuning";

/** The small quick bump of a substep accumulation (`bumpAmount`): peak scale and each half's seconds. */
const BUMP = { scale: 1.12, halfSeconds: 0.06 } as const;

/**
 * FLARE win-amount label variant (the Cocos template's `WinAmountLabelFlareAnimator`): it appears BIG and settles to
 * normal scale, fades in, and FLARES the number bright → normal (an `ExposureFilter` on the digits, ramped from
 * `shinePeak` to 0), then holds at full until the layer's `hide()` fades it out.
 *
 * With a `breakdown` (a line the server multiplied), it appears as "base x mult", holds `multiplier.holdSeconds`, then
 * POP-REPLACES: a scale bump 1 → `popScale` → 1 that swaps the text to the final amount at its peak.
 *
 * Every motion runs on the injected runner (engine time: paused, frame-stepped and speed-scaled with the win
 * presentation) and is generation-guarded: a new show / merge / pop / bump / hide stops the one before it. The filter
 * sits on the digits' own node only while a flare runs.
 */
export class WinAmountLabelFlare extends WinAmountLabel {
  private flareFilter: ExposureFilter | null = null;

  public override show(text: string, breakdown?: LabelBreakdown): void {
    const t = this.tuning as WinAmountLabelFlareTuning | null;
    const runner = this.taskRunner;
    if (!t || !runner) {
      this.applyText(text);
      this.node.alpha = 1;
      this.node.scale.set(1, 1);
      return;
    }
    const isCurrent = this.nextGeneration();
    if (breakdown && t.multiplier) {
      void this.runBreakdown(text, breakdown, t, runner, isCurrent);
      return;
    }
    this.playAppear(text, t, runner, isCurrent);
  }

  /** The deferred merge: the shown base swaps to "base x mult", re-flares, then pop-replaces to the final amount. */
  public override async mergeToFinal(finalText: string, breakdown: LabelBreakdown): Promise<void> {
    const t = this.tuning as WinAmountLabelFlareTuning | null;
    const runner = this.taskRunner;
    if (!t || !t.multiplier || !runner) {
      this.applyText(finalText);
      return;
    }
    const isCurrent = this.nextGeneration();
    this.applyText(`${breakdown.baseText} x${breakdown.multiplier}`);
    this.playFlare(runner, t, isCurrent);
    if (t.multiplier.holdSeconds > 0) {
      await runner.sleep(t.multiplier.holdSeconds);
      if (!isCurrent()) return;
    }
    await this.popTo(finalText, t.multiplier, runner, isCurrent);
  }

  /** The shown amount pop-replaces straight to `finalText` (a scale bump, the text swapped at its peak), re-flaring. */
  public override async popReplaceTo(finalText: string): Promise<void> {
    const t = this.tuning as WinAmountLabelFlareTuning | null;
    const runner = this.taskRunner;
    if (!t || !t.multiplier || !runner) {
      this.applyText(finalText);
      return;
    }
    const isCurrent = this.nextGeneration();
    this.playFlare(runner, t, isCurrent);
    await this.popTo(finalText, t.multiplier, runner, isCurrent);
  }

  /** The shown amount becomes `text` with a small quick bump (a substep's running total growing). */
  public override async bumpAmount(text: string): Promise<void> {
    const runner = this.taskRunner;
    this.applyText(text);
    if (!runner) return;
    const isCurrent = this.nextGeneration();
    await this.scaleTween(runner, BUMP.halfSeconds, 1, BUMP.scale, "quadOut", isCurrent);
    if (!isCurrent()) return;
    await this.scaleTween(runner, BUMP.halfSeconds, BUMP.scale, 1, "quadIn", isCurrent);
  }

  public override hide(): Promise<void> {
    const hiding = super.hide();
    this.removeFlareFilter();
    return hiding;
  }

  public override destroy(): void {
    this.removeFlareFilter();
    super.destroy();
  }

  /** The entrance: the text set, BIG → 1, a fade-in that then holds, the flare. */
  private playAppear(text: string, t: WinAmountLabelFlareTuning, runner: PresentationTaskRunner, isCurrent: () => boolean): void {
    this.applyText(text);
    const node = this.node;
    node.alpha = 0;
    node.scale.set(t.appearScale, t.appearScale);
    void runner.tweenProgress(
      t.fadeInSeconds,
      (k) => {
        if (isCurrent()) node.alpha = k;
      },
      undefined,
      { easing: "linear" },
    );
    void this.scaleTween(runner, t.appearScaleSeconds, t.appearScale, 1, t.appearScaleEasing, isCurrent);
    this.playFlare(runner, t, isCurrent);
  }

  /** "base x mult" appears and holds, then pop-replaces to the final amount; then holds until `hide()`. */
  private async runBreakdown(finalText: string, breakdown: LabelBreakdown, t: WinAmountLabelFlareTuning, runner: PresentationTaskRunner, isCurrent: () => boolean): Promise<void> {
    const multiplier = t.multiplier!;
    this.playAppear(`${breakdown.baseText} x${breakdown.multiplier}`, t, runner, isCurrent);
    await runner.sleep(multiplier.holdSeconds);
    if (!isCurrent()) return;
    await this.popTo(finalText, multiplier, runner, isCurrent);
  }

  /** The pop-replace bump: 1 → `popScale` with the text swapped to `finalText` at the peak, → 1. */
  private async popTo(finalText: string, multiplier: NonNullable<WinAmountLabelFlareTuning["multiplier"]>, runner: PresentationTaskRunner, isCurrent: () => boolean): Promise<void> {
    const half = Math.max(0.001, multiplier.popSeconds / 2);
    await this.scaleTween(runner, half, 1, multiplier.popScale, "quadOut", isCurrent);
    if (!isCurrent()) return;
    this.applyText(finalText);
    await this.scaleTween(runner, half, multiplier.popScale, 1, "quadIn", isCurrent);
  }

  private scaleTween(runner: PresentationTaskRunner, seconds: number, from: number, to: number, easing: WinAmountLabelFlareTuning["appearScaleEasing"], isCurrent: () => boolean): Promise<void> {
    const node = this.node;
    return runner.tweenProgress(
      seconds,
      (k) => {
        if (!isCurrent()) return;
        const scale = from + (to - from) * k;
        node.scale.set(scale, scale);
      },
      undefined,
      { easing },
    );
  }

  /** The digits flare from `shinePeak` back to normal; nothing without digits or a positive peak. */
  private playFlare(runner: PresentationTaskRunner, t: WinAmountLabelFlareTuning, isCurrent: () => boolean): void {
    const digits = this.digits();
    if (!digits || t.shinePeak <= 0) return;
    const filter = (this.flareFilter ??= new ExposureFilter(0));
    filter.highlight = t.shinePeak;
    if (!digits.filters?.includes(filter)) digits.filters = [...(digits.filters ?? []), filter];
    void runner
      .tweenProgress(
        t.shineSeconds,
        (k) => {
          if (isCurrent()) filter.highlight = t.shinePeak * (1 - k);
        },
        undefined,
        { easing: t.shineEasing },
      )
      .then(() => {
        if (isCurrent()) this.removeFlareFilter();
      });
  }

  private removeFlareFilter(): void {
    const filter = this.flareFilter;
    const digits = this.digits();
    if (!filter || !digits || digits.destroyed) return;
    filter.highlight = 0;
    if (digits.filters?.includes(filter)) digits.filters = digits.filters.filter((f) => f !== filter);
  }

  /** What draws the amount: the sprite text, else the Pixi text. */
  private digits(): Container | null {
    return this.spriteText ?? this.label;
  }

  /** Starts a new motion: the ones before it stop writing. */
  private nextGeneration(): () => boolean {
    const generation = ++this.generation;
    return () => this.generation === generation && !this.node.destroyed;
  }
}
